import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import * as Sentry from '@sentry/react';
import Sidebar from './components/Sidebar';
import Clientes from './pages/Clientes';
import Servicos from './pages/Servicos';
import Obras from './pages/Obras';
import Dashboard from './pages/Dashboard';
import FormasPagamento from './pages/FormasPagamento';
import Configuracoes from './pages/Configuracoes';
import Financeiro from './pages/Financeiro';
import Plano from './pages/Plano';
import Login from './pages/Login';
import EsqueciSenha from './pages/EsqueciSenha';
import RedefinirSenha from './pages/RedefinirSenha';
import ConfirmarEmail from './pages/ConfirmarEmail';
import Usuarios from './pages/Usuarios';
import Empresa from './pages/Empresa';
import Perfil from './pages/Perfil';
import SuperAdmin from './pages/SuperAdmin';
import BuscaGlobal from './components/BuscaGlobal';
import ModalTermos from './components/ModalTermos';
import useThema from './hooks/useThema';
import api from './services/api';
import './index.css';

function App() {
  const navigate = useNavigate();
  const location = useLocation();
  const [usuario, setUsuario] = useState(() => {
    try { return JSON.parse(sessionStorage.getItem('usuario')); } catch { return null; }
  });
  const [origemNavegacao, setOrigemNavegacao] = useState(null);
  const [badgeObras, setBadgeObras] = useState(0);
  const [sidebarAberta, setSidebarAberta] = useState(false);
  const [tenantStatus, setTenantStatus] = useState(null);
  const [mensagemSuspensao, setMensagemSuspensao] = useState(null);
  const [avisos, setAvisos] = useState([]);
  const { tema, alternarTema } = useThema();
  const avisosTimerRef = useRef(null);

  const atualizarBadge = useCallback(async () => {
    try {
      const res = await api.get('/home/atencao');
      const total = res.data.obras_atrasadas.length + res.data.pagamentos_vencidos.length;
      setBadgeObras(total);
    } catch { }
  }, []);

  useEffect(() => {
    // Atualiza permissões frescas do servidor sempre que o app carrega
    if (usuario) {
      api.get('/auth/perfil').then(res => {
        const atualizado = { ...usuario, permissoes: res.data.permissoes, role: res.data.role };
        sessionStorage.setItem('usuario', JSON.stringify(atualizado));
        setUsuario(atualizado);
      }).catch(() => {});
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (usuario?.role === 'superadmin') return;
    atualizarBadge();
    const intervalo = setInterval(atualizarBadge, 5 * 60 * 1000);
    return () => clearInterval(intervalo);
  }, [atualizarBadge, usuario]);

  useEffect(() => {
    if (usuario && usuario.role !== 'superadmin') {
      api.get('/tenant/status').then(res => setTenantStatus(res.data)).catch(() => {});
    }
  }, [usuario]);

  useEffect(() => {
    if (usuario) {
      Sentry.setUser({
        email: usuario.email,
        id: String(usuario.usuarioid),
        username: usuario.nome,
        tenant_id: usuario.tenant_id,
      });
    } else {
      Sentry.setUser(null);
    }
  }, [usuario]);

  useEffect(() => {
    const handler = () => { setUsuario(null); navigate('/login'); };
    window.addEventListener('auth-logout', handler);
    return () => window.removeEventListener('auth-logout', handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const handler = (e) => setMensagemSuspensao(e.detail?.mensagem || 'Sua assinatura está suspensa.');
    window.addEventListener('conta-suspensa', handler);
    return () => window.removeEventListener('conta-suspensa', handler);
  }, []);

  function handleLogout() {
    api.post('/auth/logout').catch(() => {});
    sessionStorage.removeItem('token');
    sessionStorage.removeItem('usuario');
    setUsuario(null);
    navigate('/login');
  }

  // Busca avisos ativos a cada 5 minutos
  useEffect(() => {
    if (!usuario || usuario.role === 'superadmin') return;
    function buscarAvisos() {
      api.get('/avisos-sistema/ativos').then(r => setAvisos(r.data)).catch(() => {});
    }
    buscarAvisos();
    avisosTimerRef.current = setInterval(buscarAvisos, 5 * 60 * 1000);
    return () => clearInterval(avisosTimerRef.current);
  }, [usuario]);

  // Logout automático às 23:59 todo dia
  useEffect(() => {
    if (!usuario) return;
    function verificarHora() {
      const agora = new Date();
      if (agora.getHours() === 23 && agora.getMinutes() === 59) {
        api.post('/auth/logout').catch(() => {});
        sessionStorage.removeItem('token');
        sessionStorage.removeItem('usuario');
        setUsuario(null);
        navigate('/login');
      }
    }
    const id = setInterval(verificarHora, 60 * 1000);
    return () => clearInterval(id);
  }, [usuario]);

  function navegarPara(novaPagina, obraId, origem) {
    if (origem) setOrigemNavegacao(origem);
    navigate(obraId ? `/obras/${obraId}` : `/${novaPagina}`);
  }

  if (!usuario) {
    if (location.pathname === '/esqueci-senha') return <EsqueciSenha />;
    if (location.pathname.startsWith('/resetar-senha')) {
      return (
        <Routes>
          <Route path="/resetar-senha/:token" element={<RedefinirSenha />} />
          <Route path="*" element={<RedefinirSenha />} />
        </Routes>
      );
    }
    if (location.pathname.startsWith('/confirmar-email')) {
      return (
        <Routes>
          <Route path="/confirmar-email/:token" element={<ConfirmarEmail />} />
          <Route path="*" element={<ConfirmarEmail />} />
        </Routes>
      );
    }
    return <Login onLogin={u => {
      setUsuario(u);
      navigate(u.role === 'superadmin' ? '/superadmin' : '/dashboard');
    }} />;
  }

  // Painel exclusivo do super admin — sem sidebar, sem tenant
  if (usuario.role === 'superadmin') {
    return <SuperAdmin onLogout={handleLogout} />;
  }

  // Aguarda o status do tenant carregar antes de verificar termos
  const termosAceitos = tenantStatus === null || !!tenantStatus?.termos_aceitos_em;

  const suspensa = !!mensagemSuspensao;

  return (
    <div className="layout">
      {!termosAceitos && (
        <ModalTermos onAceito={() => setTenantStatus(s => ({ ...s, termos_aceitos_em: new Date().toISOString() }))} />
      )}
      {sidebarAberta && <div className="sidebar-overlay" onClick={() => setSidebarAberta(false)} />}
      <Sidebar
        paginaAtiva={suspensa ? 'plano' : (location.pathname.slice(1).split('/')[0] || 'dashboard')}
        setPagina={(p, obraId, origem) => { if (suspensa) { navegarPara('plano'); } else { navegarPara(p, obraId, origem); } setSidebarAberta(false); }}
        badgeObras={badgeObras}
        usuario={usuario}
        onLogout={handleLogout}
        tema={tema}
        onAlternarTema={alternarTema}
        aberta={sidebarAberta}
        onFechar={() => setSidebarAberta(false)}
        tenantStatus={tenantStatus}
      />
      <div className="conteudo">
        {avisos.map(aviso => {
          const cores = {
            manutencao: { bg: '#fffbeb', cor: '#92400e', borda: '#fde68a', icone: '🔧' },
            atualizacao: { bg: '#f0fdf4', cor: '#065f46', borda: '#86efac', icone: '🚀' },
            info:        { bg: '#eff6ff', cor: '#1e40af', borda: '#93c5fd', icone: 'ℹ️' },
          };
          const c = cores[aviso.tipo] || cores.info;
          return (
            <div key={aviso.id} style={{ background: c.bg, borderBottom: `1px solid ${c.borda}`, padding: '10px 20px', marginBottom: 8, fontSize: 13, color: c.cor, display: 'flex', alignItems: 'center', gap: 8 }}>
              <span>{c.icone}</span>
              <span style={{ flex: 1 }}>{aviso.mensagem}</span>
              <span style={{ fontSize: 11, opacity: 0.7, whiteSpace: 'nowrap' }}>
                {new Date(aviso.criado_em).toLocaleDateString('pt-BR')}
              </span>
            </div>
          );
        })}
        {suspensa && (() => {
          const dataVenc = tenantStatus?.data_vencimento ? new Date(tenantStatus.data_vencimento) : null;
          const diasParaExclusao = dataVenc
            ? Math.max(0, 60 - Math.floor((new Date() - dataVenc) / (1000 * 60 * 60 * 24)))
            : null;
          const dataExclusao = dataVenc ? new Date(dataVenc.getTime() + 60 * 24 * 60 * 60 * 1000).toLocaleDateString('pt-BR') : null;
          return (
            <div style={{ background: '#7f1d1d', borderBottom: '1px solid #dc2626', padding: '12px 20px', fontSize: 13, color: '#fca5a5' }}>
              <strong>🔒 {mensagemSuspensao}</strong>
              {diasParaExclusao !== null && (
                <span style={{ marginLeft: 12, color: '#fca5a5' }}>
                  ⚠️ Todas as suas obras, usuários e dados financeiros serão <strong>deletados permanentemente</strong> em <strong>{diasParaExclusao} dias ({dataExclusao})</strong> caso a conta não seja reativada. Renove agora na aba Plano para não perder seus dados.
                </span>
              )}
            </div>
          );
        })()}
        <div className="topbar-mobile">
          <button className="hamburger-btn" onClick={() => setSidebarAberta(true)} aria-label="Abrir menu">
            <span /><span /><span />
          </button>
          <span className="topbar-titulo">CronoObra</span>
          <button
            className="busca-mobile-btn"
            onClick={() => window.dispatchEvent(new Event('busca-abrir'))}
            aria-label="Buscar"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/>
            </svg>
          </button>
        </div>
        {suspensa ? <Plano /> : (
          <Routes>
            <Route path="/" element={<Navigate to="/dashboard" replace />} />
            <Route path="/dashboard" element={<Dashboard navegarPara={navegarPara} />} />
            <Route path="/contatos" element={<Clientes />} />
            <Route path="/clientes" element={<Clientes />} />
            <Route path="/servicos" element={<Servicos />} />
            <Route path="/obras" element={<Obras origemNavegacao={origemNavegacao} />} />
            <Route path="/obras/:id" element={<Obras origemNavegacao={origemNavegacao} />} />
            <Route path="/formas-pagamento" element={<FormasPagamento />} />
            <Route path="/configuracoes" element={<Configuracoes />} />
            <Route path="/empresa" element={<Empresa />} />
            <Route path="/plano" element={<Plano />} />
            <Route path="/financeiro" element={<Financeiro />} />
            <Route path="/usuarios" element={<Usuarios />} />
            <Route path="/perfil" element={<Perfil onPerfilAtualizado={u => setUsuario(u)} />} />
            <Route path="*" element={<Navigate to="/dashboard" replace />} />
          </Routes>
        )}
      </div>
      <BuscaGlobal />
    </div>
  );
}

export default App;
